<template>
  <div class="py-4 container-fluid">
    <!-- Period selector -->
    <div class="card mb-4">
      <div class="card-body d-flex align-items-center flex-wrap gap-3">
        <h6 class="mb-0 me-3">Métricas Detalladas</h6>
        <input
          v-if="selectedPeriod === 'day' || selectedPeriod === 'week' || selectedPeriod === 'month'"
          v-model="selectedDate"
          type="date"
          class="form-control form-control-sm"
          style="width: auto;"
          @change="loadAll"
        />
        <input
          v-if="selectedPeriod === 'year'"
          v-model="selectedYear"
          type="number"
          min="2020"
          max="2099"
          class="form-control form-control-sm"
          style="width: 100px;"
          @change="loadAll"
        />
        <span v-if="summaryData" class="text-sm text-secondary">
          {{ summaryData.range_label }}
        </span>
        <div class="btn-group ms-auto" role="group">
          <button
            v-for="opt in periodOptions"
            :key="opt.value"
            type="button"
            class="btn btn-sm mb-0"
            :class="selectedPeriod === opt.value ? 'btn-primary' : 'btn-outline-primary'"
            @click="changePeriod(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Indicator cards -->
    <div class="row g-4 mb-4" v-if="summaryData">
      <div
        v-for="card in metricCards"
        :key="card.key"
        class="col-12 col-sm-6 col-lg-4 col-xl-2-4"
      >
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="d-flex align-items-center mb-2">
              <div
                class="icon icon-shape icon-sm me-2"
                :class="card.iconBg"
              >
                <i class="ni" :class="card.icon"></i>
              </div>
              <p class="text-xs text-secondary mb-0 font-weight-bold">{{ card.title }}</p>
            </div>
            <h4 class="mb-1">{{ formatMetric(card.key, card.isCount) }}</h4>
            <div class="d-flex align-items-center gap-1">
              <span
                class="text-xs font-weight-bold"
                :class="variationClass(card.key)"
              >
                {{ variationArrow(card.key) }} {{ Math.abs(summaryData.metrics[card.key].variation_percent) }}%
              </span>
              <span class="text-xs text-secondary">vs anterior: {{ formatMetricPrevious(card.key, card.isCount) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts row -->
    <div class="row g-4 mb-4">
      <div class="col-12 col-lg-6">
        <div class="card border-0 shadow-sm">
          <div class="card-header pb-0">
            <h6 class="mb-0">Comparativa Mensual</h6>
            <p class="text-sm text-secondary mb-0">Mes actual vs mes anterior</p>
          </div>
          <div class="card-body p-3">
            <div style="height: 300px;">
              <canvas ref="monthlyChartCanvas"></canvas>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-6">
        <div class="card border-0 shadow-sm">
          <div class="card-header pb-0">
            <h6 class="mb-0">Comparativa Anual</h6>
            <p class="text-sm text-secondary mb-0">Año actual vs año anterior</p>
          </div>
          <div class="card-body p-3">
            <div style="height: 300px;">
              <canvas ref="yearlyChartCanvas"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Weekly breakdown table -->
    <div class="card mb-4">
      <div class="card-header pb-0">
        <h6 class="mb-0">Desglose Semanal</h6>
      </div>
      <div class="card-body px-0 pt-0 pb-2">
        <div class="table-responsive p-0">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Semana</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Repuestos</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Mano de Obra</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Motos</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Egresos</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ganancia</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Variación</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="week in weeklyData" :key="week.week">
                <td class="text-sm">{{ week.label }}</td>
                <td class="text-center text-sm">{{ formatMoney(week.parts_revenue) }}</td>
                <td class="text-center text-sm">{{ formatMoney(week.labor_revenue) }}</td>
                <td class="text-center text-sm">{{ week.motorcycles_count }}</td>
                <td class="text-center text-sm text-danger">{{ formatMoney(week.money_outflow) }}</td>
                <td class="text-center text-sm font-weight-bold text-success">{{ formatMoney(week.profit) }}</td>
                <td class="text-center text-sm">
                  <span :class="week.variation_profit >= 0 ? 'text-success' : 'text-danger'">
                    {{ week.variation_profit >= 0 ? '↑' : '↓' }} {{ Math.abs(week.variation_profit) }}%
                  </span>
                </td>
              </tr>
              <tr v-if="!weeklyData || weeklyData.length === 0">
                <td colspan="7" class="text-center text-xxs">No hay registros disponibles</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Daily breakdown table -->
    <div class="card mb-4">
      <div class="card-header pb-0">
        <h6 class="mb-0">Desglose Diario</h6>
      </div>
      <div class="card-body px-0 pt-0 pb-2">
        <div class="table-responsive p-0" style="max-height: 400px; overflow-y: auto;">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Día</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Fecha</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Repuestos</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Mano de Obra</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Motos</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Egresos</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ganancia</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Variación</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="day in dailyData" :key="day.date">
                <td class="text-sm">{{ day.weekday }} {{ day.day }}</td>
                <td class="text-center text-sm">{{ formatDate(day.date) }}</td>
                <td class="text-center text-sm">{{ formatMoney(day.parts_revenue) }}</td>
                <td class="text-center text-sm">{{ formatMoney(day.labor_revenue) }}</td>
                <td class="text-center text-sm">{{ day.motorcycles_count }}</td>
                <td class="text-center text-sm text-danger">{{ formatMoney(day.money_outflow) }}</td>
                <td class="text-center text-sm font-weight-bold text-success">{{ formatMoney(day.profit) }}</td>
                <td class="text-center text-sm">
                  <span :class="day.variation_profit >= 0 ? 'text-success' : 'text-danger'">
                    {{ day.variation_profit >= 0 ? '↑' : '↓' }} {{ Math.abs(day.variation_profit) }}%
                  </span>
                </td>
              </tr>
              <tr v-if="!dailyData || dailyData.length === 0">
                <td colspan="8" class="text-center text-xxs">No hay registros disponibles</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { markRaw } from 'vue';
import { colombiaTodayString, formatDateOnly } from '@/helpers/dateFormat';

export default {
  name: 'MetricsDashboard',
  data() {
    return {
      selectedPeriod: 'month',
      selectedDate: colombiaTodayString(),
      selectedYear: new Date().getFullYear(),
      summaryData: null,
      monthlyData: null,
      weeklyData: [],
      dailyData: [],
      yearlyData: null,
      monthlyChart: null,
      yearlyChart: null,
      periodOptions: [
        { value: 'day', label: 'Día' },
        { value: 'week', label: 'Semana' },
        { value: 'month', label: 'Mes' },
        { value: 'year', label: 'Año' },
      ],
      metricCards: [
        { key: 'parts_revenue', title: 'Entradas Repuestos', icon: 'ni-box-2', iconBg: 'bg-gradient-info', isCount: false },
        { key: 'labor_revenue', title: 'Entradas Mano de Obra', icon: 'ni-hat-3', iconBg: 'bg-gradient-warning', isCount: false },
        { key: 'motorcycles_count', title: 'Motos Ingresadas', icon: 'ni-delivery-fast', iconBg: 'bg-gradient-primary', isCount: true },
        { key: 'money_outflow', title: 'Salidas de Dinero', icon: 'ni-money-coins', iconBg: 'bg-gradient-danger', isCount: false },
        { key: 'profit', title: 'Ganancia del Periodo', icon: 'ni-chart-pie-35', iconBg: 'bg-gradient-success', isCount: false },
      ],
    };
  },
  computed: {
    currentUser() {
      try {
        const stored = localStorage.getItem('user');
        return stored ? JSON.parse(stored) : {};
      } catch {
        return {};
      }
    },
    isAdmin() {
      return String(this.currentUser?.role ?? '').trim() === '1';
    },
  },
  methods: {
    formatMoney(value) {
      return Number(value || 0).toLocaleString('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    formatDate(value) {
      return formatDateOnly(value) || '—';
    },
    formatMetric(key, isCount) {
      if (!this.summaryData) return '—';
      const val = this.summaryData.metrics[key]?.current ?? 0;
      if (isCount) return String(Math.round(val));
      return this.formatMoney(val);
    },
    formatMetricPrevious(key, isCount) {
      if (!this.summaryData) return '—';
      const val = this.summaryData.metrics[key]?.previous ?? 0;
      if (isCount) return String(Math.round(val));
      return this.formatMoney(val);
    },
    variationClass(key) {
      if (!this.summaryData) return 'text-secondary';
      const pct = this.summaryData.metrics[key]?.variation_percent ?? 0;
      return pct >= 0 ? 'text-success' : 'text-danger';
    },
    variationArrow(key) {
      if (!this.summaryData) return '';
      const pct = this.summaryData.metrics[key]?.variation_percent ?? 0;
      return pct >= 0 ? '↑' : '↓';
    },
    buildParams() {
      const params = { period: this.selectedPeriod };
      if (this.selectedPeriod === 'day' || this.selectedPeriod === 'week' || this.selectedPeriod === 'month') {
        if (this.selectedDate) params.date = this.selectedDate;
      }
      if (this.selectedPeriod === 'year') {
        params.date = String(this.selectedYear);
      }
      return params;
    },
    async loadSummary() {
      try {
        const response = await this.$store.dispatch('metrics/getSummary', this.buildParams());
        this.summaryData = response.data;
      } catch (error) {
        console.error('Error loading metrics summary:', error);
      }
    },
    async loadMonthlyComparison() {
      try {
        const year = this.selectedPeriod === 'year' ? this.selectedYear : new Date().getFullYear();
        const response = await this.$store.dispatch('metrics/getMonthlyComparison', { year });
        this.monthlyData = response.data;
        this.renderMonthlyChart();
      } catch (error) {
        console.error('Error loading monthly comparison:', error);
      }
    },
    async loadWeeklyBreakdown() {
      try {
        const now = new Date();
        const month = now.getMonth() + 1;
        const year = this.selectedPeriod === 'year' ? this.selectedYear : now.getFullYear();
        const response = await this.$store.dispatch('metrics/getWeeklyBreakdown', { month, year });
        this.weeklyData = response.data?.weeks ?? [];
      } catch (error) {
        console.error('Error loading weekly breakdown:', error);
      }
    },
    async loadDailyBreakdown() {
      try {
        const now = new Date();
        const month = now.getMonth() + 1;
        const year = this.selectedPeriod === 'year' ? this.selectedYear : now.getFullYear();
        const response = await this.$store.dispatch('metrics/getDailyBreakdown', { month, year });
        this.dailyData = response.data?.days ?? [];
      } catch (error) {
        console.error('Error loading daily breakdown:', error);
      }
    },
    async loadYearlyComparison() {
      try {
        const year = this.selectedPeriod === 'year' ? this.selectedYear : new Date().getFullYear();
        const response = await this.$store.dispatch('metrics/getYearlyComparison', { year });
        this.yearlyData = response.data;
        this.renderYearlyChart();
      } catch (error) {
        console.error('Error loading yearly comparison:', error);
      }
    },
    destroyChart(chartRef) {
      if (chartRef) {
        chartRef.destroy();
      }
    },
    renderMonthlyChart() {
      this.destroyChart(this.monthlyChart);
      if (!this.monthlyData || !this.$refs.monthlyChartCanvas) return;

      const labels = this.monthlyData.months.map(m => m.month);
      const partsData = this.monthlyData.months.map(m => m.parts_revenue);
      const laborData = this.monthlyData.months.map(m => m.labor_revenue);
      const outflowData = this.monthlyData.months.map(m => m.money_outflow);
      const profitData = this.monthlyData.months.map(m => m.profit);

      const ctx = this.$refs.monthlyChartCanvas.getContext('2d');
      this.monthlyChart = markRaw(new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            { label: 'Repuestos', data: partsData, backgroundColor: 'rgba(94, 114, 228, 0.7)', borderColor: 'rgba(94, 114, 228, 1)', borderWidth: 1 },
            { label: 'Mano de Obra', data: laborData, backgroundColor: 'rgba(255, 193, 7, 0.7)', borderColor: 'rgba(255, 193, 7, 1)', borderWidth: 1 },
            { label: 'Egresos', data: outflowData, backgroundColor: 'rgba(233, 30, 99, 0.7)', borderColor: 'rgba(233, 30, 99, 1)', borderWidth: 1 },
            { label: 'Ganancia', data: profitData, backgroundColor: 'rgba(76, 175, 80, 0.7)', borderColor: 'rgba(76, 175, 80, 1)', borderWidth: 1 },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: true, position: 'bottom' } },
        },
      }));
    },
    renderYearlyChart() {
      this.destroyChart(this.yearlyChart);
      if (!this.yearlyData || !this.$refs.yearlyChartCanvas) return;

      const ctx = this.$refs.yearlyChartCanvas.getContext('2d');
      this.yearlyChart = markRaw(new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.yearlyData.labels,
          datasets: [
            {
              label: `Año ${this.yearlyData.year}`,
              data: this.yearlyData.current_year,
              borderColor: 'rgba(76, 175, 80, 1)',
              backgroundColor: 'rgba(76, 175, 80, 0.15)',
              fill: true,
              tension: 0.4,
              borderWidth: 2,
            },
            {
              label: `Año ${this.yearlyData.year - 1}`,
              data: this.yearlyData.previous_year,
              borderColor: 'rgba(94, 114, 228, 1)',
              backgroundColor: 'rgba(94, 114, 228, 0.15)',
              fill: true,
              tension: 0.4,
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: true, position: 'bottom' } },
        },
      }));
    },
    changePeriod(period) {
      this.selectedPeriod = period;
      this.loadAll();
    },
    async loadAll() {
      await Promise.all([
        this.loadSummary(),
        this.loadMonthlyComparison(),
        this.loadWeeklyBreakdown(),
        this.loadDailyBreakdown(),
        this.loadYearlyComparison(),
      ]);
    },
  },
  async mounted() {
    if (!this.isAdmin) {
      this.$router.push('/dashboard-default');
      return;
    }
    await this.loadAll();
  },
  beforeUnmount() {
    this.destroyChart(this.monthlyChart);
    this.destroyChart(this.yearlyChart);
  },
};
</script>

<style scoped>
.btn-group .btn {
  margin-bottom: 0;
}
.col-xl-2-4 {
  flex: 0 0 20%;
  max-width: 20%;
}
@media (max-width: 1200px) {
  .col-xl-2-4 {
    flex: 0 0 50%;
    max-width: 50%;
  }
}
@media (max-width: 768px) {
  .col-xl-2-4 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
</style>
